import { useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

import useLoggedInUser from 'app/hooks/useLoggedInUser';
import { getAllAnnouncements } from 'app/services/announcement';
import { readWhatsNewLastSeen, subscribeToWhatsNewSeen } from 'app/utils/whatsNewLastSeen';

const LATEST_FRESH_FOR_MS = 5 * 60 * 1000;

/** True when the newest What's New announcement is newer than the last one the user has seen. */
export default function useHasUnreadWhatsNew(): boolean {
  const user = useLoggedInUser();
  const userId = user?._id;

  const lastSeen = useSyncExternalStore(subscribeToWhatsNewSeen, () =>
    userId ? readWhatsNewLastSeen(userId) : null
  );

  const { data } = useQuery({
    queryKey: ['announcements', 'latest'],
    queryFn: () => getAllAnnouncements({ page: 0, limit: 1 }),
    enabled: !!userId,
    staleTime: LATEST_FRESH_FOR_MS,
  });

  const latestCreated: unknown = data?.announcements[0]?.created;

  // Nothing to announce, or the announcements could not be loaded: no dot either way.
  if (typeof latestCreated !== 'string') return false;
  if (!lastSeen) return true;

  return new Date(latestCreated).getTime() > new Date(lastSeen).getTime();
}
